// PR-3634 contracts v4 §6.5: the trial emails link to /react/?openPlans=pro&utm_*.
// Login.aspx always returns to the dashboard and drops the query, and the first API call that answers 401
// sends a signed-out visitor to login before any page renders. So App.js keeps the request in this tab's
// sessionStorage at module load, and ProTrialPlansLauncher opens TierPlans once the user is signed in.

export const OPEN_PLANS_STORAGE_KEY = 'pulseem.openPlans';
export const OPEN_PLANS_MAX_AGE_MS = 60 * 60 * 1000;

const OPEN_PLANS_PARAM = 'openPlans';
const OPEN_PLANS_VALUE = 'pro';

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export interface OpenPlansRequest {
  plan: string;
  ts: number;
}

const getSessionStorage = (): StorageLike | null => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch (e) {
    return null;
  }
};

// Called once at App.js module load, before any request. Never throws.
export const captureOpenPlansRequest = (
  search: string = typeof window !== 'undefined' ? window.location.search : '',
  storage: StorageLike | null = getSessionStorage(),
  now: number = Date.now()
): boolean => {
  try {
    if (!storage || new URLSearchParams(search).get(OPEN_PLANS_PARAM) !== OPEN_PLANS_VALUE) return false;
    storage.setItem(OPEN_PLANS_STORAGE_KEY, JSON.stringify({ plan: OPEN_PLANS_VALUE, ts: now }));
    return true;
  } catch (e) {
    return false;
  }
};

export const hasOpenPlansRequest = (storage: StorageLike | null = getSessionStorage()): boolean => {
  try {
    return !!storage && storage.getItem(OPEN_PLANS_STORAGE_KEY) !== null;
  } catch (e) {
    return false;
  }
};

// Removes the request and returns it, or null when there is none, it is unreadable or older than 60 minutes.
export const consumeOpenPlansRequest = (
  storage: StorageLike | null = getSessionStorage(),
  now: number = Date.now()
): OpenPlansRequest | null => {
  try {
    if (!storage) return null;
    const raw = storage.getItem(OPEN_PLANS_STORAGE_KEY);
    if (raw === null) return null;
    storage.removeItem(OPEN_PLANS_STORAGE_KEY);
    const request = JSON.parse(raw);
    const age = now - Number(request?.ts);
    if (request?.plan !== OPEN_PLANS_VALUE || !(age >= 0 && age < OPEN_PLANS_MAX_AGE_MS)) return null;
    return { plan: request.plan, ts: Number(request.ts) };
  } catch (e) {
    return null;
  }
};

// The URL without openPlans and the utm_* parameters; everything else (path, other params, hash) is kept.
export const stripOpenPlansParams = (href: string): string => {
  try {
    const url = new URL(href);
    const keys: string[] = [];
    url.searchParams.forEach((_, key) => {
      if (key === OPEN_PLANS_PARAM || key.toLowerCase().indexOf('utm_') === 0) keys.push(key);
    });
    if (!keys.length) return href;
    keys.forEach((key) => url.searchParams.delete(key));
    return url.toString();
  } catch (e) {
    return href;
  }
};
